import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { get } from "lodash-es";

import { API_ENDPOINTS } from "shared/constants";
import { IInfoBlockItemType, IDataKeys, IInfoBlockItem } from "shared/types";
import { IStoreState } from "App/types";
import { InfoBlockItemService } from "shared/services";
import { VENUE_ACTIONS } from "Venues/types";
import { getInfoBlockItemKey, isPageFetchedInState } from "Venues/utils";
import { INFO_BLOCK_ITEMS_PER_PAGE } from "Venues/assets";

interface IGetVenueInfoBlockItemsData {
  venueId: string;
  type: IInfoBlockItem;
  page?: number;
  itemsPerPage?: number;
  paginationKey?: string;
}

export const dispatchVenueInfoBlockItemsAction = (
  dispatch: Dispatch<any>,
  params: IGetVenueInfoBlockItemsData
) => {
  const { venueId, type, page } = params;
  const paginationKey = getInfoBlockItemKey(venueId, type);

  return dispatch(
    getVenueInfoBlockItems({ ...params, paginationKey, page: page || 1 })
  );
};

export const getVenueInfoBlockItems = createAsyncThunk(
  VENUE_ACTIONS.getVenueInfoBlockItems,
  async (
    { venueId, type, page, itemsPerPage }: IGetVenueInfoBlockItemsData,
    { rejectWithValue }
  ) => {
    try {
      const data = (await InfoBlockItemService.getInfoBlockItems(type, {
        venue: API_ENDPOINTS.VENUE(venueId),
        ...(page && { page }),
        itemsPerPage: itemsPerPage || INFO_BLOCK_ITEMS_PER_PAGE,
      })) as any;

      return {
        [type]: data[type],
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ venueId, type, page }, { getState }) => {
      const state = getState() as IStoreState;

      // do not refetch if equipment page has already been fetched
      return !isPageFetchedInState(
        state,
        getInfoBlockItemKey(venueId, type),
        page || 1
      );
    },
  }
);

export const createInfoBlockItem = createAsyncThunk(
  VENUE_ACTIONS.createInfoBlockItem,
  async (
    { item, type }: { item: Partial<IInfoBlockItemType>; type: IInfoBlockItem },
    { rejectWithValue }
  ) => {
    try {
      const data = await InfoBlockItemService.createInfoBlockItem(item, type);

      const createdItem = (Object.values(data?.[type]) as any)?.[0];

      return { createdItem, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getInfoBlockItem = createAsyncThunk(
  VENUE_ACTIONS.getInfoBlockItem,
  async (
    { id, type }: { id: string; type: IInfoBlockItem },
    { rejectWithValue, getState }
  ) => {
    try {
      // todo: add as condition, see infoBlock.getInfoBlock
      const itemExisits = get(getState() as IStoreState, [
        "data",
        "data",
        type,
        API_ENDPOINTS.INFO_BLOCK_ITEM(id, type),
      ]);

      if (itemExisits) {
        return;
      }

      return InfoBlockItemService.getInfoBlockItem(id, type);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editInfoBlockItem = createAsyncThunk(
  VENUE_ACTIONS.editInfoBlockItem,
  async (
    {
      id,
      item,
      type,
    }: { id: string; item: Partial<IInfoBlockItemType>; type: IInfoBlockItem },
    { rejectWithValue }
  ) => {
    try {
      return await InfoBlockItemService.editInfoBlockItem(id, item, type);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteInfoBlockItem = createAsyncThunk(
  VENUE_ACTIONS.deleteInfoBlockItem,
  async (
    {
      id,
      type,
    }: {
      id: string;
      type: IInfoBlockItem;
      venueInfoBlockId?: string;
      stageInfoBlockId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      await InfoBlockItemService.deleteInfoBlockItem(id, type);
      return {
        path: API_ENDPOINTS.INFO_BLOCK_ITEM(id, type),
        dataKey: IDataKeys[type],
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
